/**
 * DocumentTagsPanel — coach manages custom merge tags for document templates.
 *
 * Shows system tags (read-only, auto-populated) and coach-defined custom tags
 * (editable). Each tag renders as {{key}} for pasting into DOCX templates.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  DocumentTemplateTag,
  SystemTag,
  listTemplateTags,
  listSystemTags,
  createTemplateTag,
  updateTemplateTag,
  deleteTemplateTag,
} from '../services/documentService';

const TAG_KEY_RE = /^[a-z][a-z0-9_]{0,49}$/;

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

interface EditingState {
  id: string;
  label: string;
  default_value: string;
}

interface Props {
  scrollTo?: 'system' | 'custom';
}

const DocumentTagsPanel: React.FC<Props> = ({ scrollTo }) => {
  const systemRef = useRef<HTMLElement>(null);
  const customRef = useRef<HTMLElement>(null);
  const [customTags, setCustomTags] = useState<DocumentTemplateTag[]>([]);
  const [systemTags, setSystemTags] = useState<SystemTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newDefault, setNewDefault] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [editing, setEditing] = useState<EditingState | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [custom, system] = await Promise.all([listTemplateTags(), listSystemTags()]);
      setCustomTags(custom);
      setSystemTags(system);
    } catch (e: any) {
      setError(e.message || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!scrollTo) return;
    const ref = scrollTo === 'system' ? systemRef : customRef;
    setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }, [scrollTo]);

  const handleCopy = (key: string) => {
    copyToClipboard(`{{${key}}}`);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!TAG_KEY_RE.test(newKey)) {
      setFormError('Key must be lowercase letters, numbers, or underscores, starting with a letter (max 50 chars).');
      return;
    }
    setSaving(true);
    try {
      const tag = await createTemplateTag(newKey.trim(), newLabel.trim(), newDefault.trim());
      setCustomTags(prev => [...prev, tag].sort((a, b) => a.label.localeCompare(b.label)));
      setNewKey(''); setNewLabel(''); setNewDefault('');
      setShowForm(false);
    } catch (e: any) {
      setFormError(e.message || 'Failed to create tag');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    try {
      const updated = await updateTemplateTag(editing.id, {
        label: editing.label,
        default_value: editing.default_value,
      });
      setCustomTags(prev => prev.map(t => t.id === updated.id ? updated : t));
      setEditing(null);
    } catch (e: any) {
      alert(e.message || 'Failed to update tag');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (tag: DocumentTemplateTag) => {
    if (!window.confirm(`Delete tag "{{${tag.key}}}"? Templates using this tag will no longer be filled for this field.`)) return;
    try {
      await deleteTemplateTag(tag.id);
      setCustomTags(prev => prev.filter(t => t.id !== tag.id));
    } catch (e: any) {
      alert(e.message || 'Failed to delete tag');
    }
  };

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-400" />
    </div>
  );

  return (
    <div className="space-y-7 text-gray-200">
      {error && <div className="p-3 bg-red-900/40 border border-red-700 rounded-lg text-red-300 text-sm">{error}</div>}

      {/* How to use */}
      <div className="p-4 bg-primary-900/30 border border-primary-800/60 rounded-lg text-sm text-primary-200">
        <p className="font-semibold text-primary-100 mb-1">How to use template tags</p>
        <p className="text-primary-300 leading-relaxed">
          Insert <code className="bg-primary-800/60 text-primary-100 px-1.5 py-0.5 rounded font-mono text-xs">{'{{tag_name}}'}</code> anywhere in a <strong className="text-primary-200">.docx</strong> template. When you send the document to a client, CoachSight ECR replaces each tag with its value automatically. Copy a tag below and paste it into your Word document.
        </p>
      </div>

      {/* System tags */}
      <section ref={systemRef}>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-dark-muted mb-3">
          System tags <span className="normal-case font-normal text-dark-muted/70">— auto-populated, no setup needed</span>
        </h3>
        <div className="rounded-lg border border-dark-border overflow-hidden">
          {/* header */}
          <div className="grid grid-cols-[180px_140px_1fr_72px] gap-0 px-4 py-2 bg-dark-sidebar border-b border-dark-border">
            <span className="text-xs font-medium text-dark-muted">Tag</span>
            <span className="text-xs font-medium text-dark-muted">Label</span>
            <span className="text-xs font-medium text-dark-muted">Description</span>
            <span></span>
          </div>
          <div className="divide-y divide-dark-border">
            {systemTags.map(tag => (
              <div key={tag.key} className="grid grid-cols-[180px_140px_1fr_72px] gap-0 items-center px-4 py-2.5 bg-dark-card hover:bg-dark-hover transition-colors">
                <code className="text-xs bg-dark-sidebar text-primary-300 px-2 py-0.5 rounded font-mono border border-dark-border w-fit">
                  {`{{${tag.key}}}`}
                </code>
                <span className="text-sm text-gray-300">{tag.label}</span>
                <span className="text-xs text-dark-muted pr-4">{tag.description}</span>
                <button
                  onClick={() => handleCopy(tag.key)}
                  className={`text-xs rounded px-2 py-1 border transition-colors justify-self-end ${
                    copiedKey === tag.key
                      ? 'bg-green-800/40 border-green-700 text-green-300'
                      : 'border-dark-border text-dark-muted hover:text-primary-300 hover:border-primary-700'
                  }`}
                >
                  {copiedKey === tag.key ? 'Copied!' : 'Copy'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom tags */}
      <section ref={customRef}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-dark-muted">
            Your custom tags <span className="normal-case font-normal text-dark-muted/70">({customTags.length})</span>
          </h3>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="text-xs text-primary-300 hover:text-primary-200 border border-primary-800 hover:border-primary-600 rounded px-3 py-1.5 transition-colors"
            >
              + Add tag
            </button>
          )}
        </div>

        {/* Add form */}
        {showForm && (
          <form onSubmit={handleCreate} className="mb-4 p-4 border border-primary-800/60 rounded-lg bg-primary-900/20 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Tag key <span className="text-dark-muted">(used in doc)</span>
                </label>
                <input
                  value={newKey}
                  onChange={e => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="session_cost"
                  className="w-full text-sm bg-dark-sidebar border border-dark-border rounded px-2.5 py-1.5 font-mono text-gray-200 placeholder-dark-muted focus:outline-none focus:border-primary-600"
                  required
                />
                <p className="text-xs text-dark-muted mt-0.5">
                  Paste as <code className="text-primary-400 font-mono">{`{{${newKey || 'key'}}}`}</code>
                </p>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Display label</label>
                <input
                  value={newLabel}
                  onChange={e => setNewLabel(e.target.value)}
                  placeholder="Session Cost"
                  className="w-full text-sm bg-dark-sidebar border border-dark-border rounded px-2.5 py-1.5 text-gray-200 placeholder-dark-muted focus:outline-none focus:border-primary-600"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Default value <span className="text-dark-muted">(overridable per send)</span>
                </label>
                <input
                  value={newDefault}
                  onChange={e => setNewDefault(e.target.value)}
                  placeholder="$150 per session"
                  className="w-full text-sm bg-dark-sidebar border border-dark-border rounded px-2.5 py-1.5 text-gray-200 placeholder-dark-muted focus:outline-none focus:border-primary-600"
                />
              </div>
            </div>
            {formError && <p className="text-xs text-red-400">{formError}</p>}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="text-sm bg-primary-700 hover:bg-primary-600 text-white rounded px-3 py-1.5 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving…' : 'Save tag'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setFormError(''); }}
                className="text-sm text-dark-muted hover:text-gray-300 px-3 py-1.5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {customTags.length === 0 && !showForm ? (
          <div className="text-center py-10 text-dark-muted text-sm border border-dashed border-dark-border rounded-lg">
            No custom tags yet. Add one to start using merge fields in your templates.
          </div>
        ) : (
          <div className="rounded-lg border border-dark-border overflow-hidden divide-y divide-dark-border">
            {customTags.map(tag => (
              <div key={tag.id} className="px-4 py-3 bg-dark-card hover:bg-dark-hover transition-colors">
                {editing?.id === tag.id ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <code className="text-xs bg-dark-sidebar text-primary-300 px-2 py-0.5 rounded font-mono border border-dark-border flex-shrink-0">
                      {`{{${tag.key}}}`}
                    </code>
                    <input
                      value={editing.label}
                      onChange={e => setEditing(prev => prev ? { ...prev, label: e.target.value } : prev)}
                      className="text-sm bg-dark-sidebar border border-dark-border rounded px-2.5 py-1 w-40 text-gray-200 focus:outline-none focus:border-primary-600"
                      placeholder="Label"
                    />
                    <input
                      value={editing.default_value}
                      onChange={e => setEditing(prev => prev ? { ...prev, default_value: e.target.value } : prev)}
                      className="text-sm bg-dark-sidebar border border-dark-border rounded px-2.5 py-1 flex-1 min-w-32 text-gray-200 focus:outline-none focus:border-primary-600"
                      placeholder="Default value"
                    />
                    <button
                      onClick={handleSaveEdit}
                      disabled={editSaving}
                      className="text-xs bg-primary-700 hover:bg-primary-600 text-white rounded px-2.5 py-1 disabled:opacity-50 transition-colors"
                    >
                      {editSaving ? '…' : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditing(null)}
                      className="text-xs text-dark-muted hover:text-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-4 min-w-0">
                      <code className="text-xs bg-dark-sidebar text-primary-300 px-2 py-0.5 rounded font-mono border border-dark-border flex-shrink-0">
                        {`{{${tag.key}}}`}
                      </code>
                      <span className="text-sm font-medium text-gray-200">{tag.label}</span>
                      {tag.default_value && (
                        <span className="text-xs text-dark-muted truncate">
                          Default: <em className="text-gray-400 not-italic">{tag.default_value}</em>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleCopy(tag.key)}
                        className={`text-xs rounded px-2.5 py-1 border transition-colors ${
                          copiedKey === tag.key
                            ? 'bg-green-800/40 border-green-700 text-green-300'
                            : 'border-dark-border text-dark-muted hover:text-primary-300 hover:border-primary-700'
                        }`}
                      >
                        {copiedKey === tag.key ? 'Copied!' : 'Copy'}
                      </button>
                      <button
                        onClick={() => setEditing({ id: tag.id, label: tag.label, default_value: tag.default_value })}
                        className="text-xs border border-dark-border text-dark-muted hover:text-gray-300 hover:border-gray-600 rounded px-2.5 py-1 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(tag)}
                        className="text-xs border border-red-900/50 text-red-500/70 hover:text-red-400 hover:border-red-800 rounded px-2.5 py-1 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default DocumentTagsPanel;
